import React, { useState, useMemo } from 'react';
import { useAnnouncements, useQuickLinks, useDocuments, useCalendars, useTeacherData, useSchedule, usePTOAllotments, usePTORequests, useCadenceTasks } from '../hooks/useFirestore';
import { VIRTUES } from '../data/virtueData';
import HomeComplianceBanner from './HomeComplianceBanner';
import { TodaysScheduleCard, findMyTeacher } from './ScheduleBuilder';
import { computeBalances, fmtPeriod } from './TimeOff';

const TODAY = new Date().toISOString().split('T')[0];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function getWeekDates() {
  const today = new Date();
  const day = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
  const dates = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
}

function timeAgo(isoStr) {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(isoStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function Home({ uid, isAdmin, displayName, masterStudents, onNavigate }) {
  const { announcements, loading: annLoading, addAnnouncement, removeAnnouncement, togglePin } = useAnnouncements();
  const { links, loading: linksLoading, addLink, removeLink } = useQuickLinks();
  const { documents, loading: docsLoading, addDocument, removeDocument } = useDocuments();
  const { calendars, addCalendar, removeCalendar } = useCalendars();
  const { classes, loading: classesLoading } = useTeacherData(uid, masterStudents);
  const { published: publishedSchedule } = useSchedule();
  const { allotments: ptoAllotments } = usePTOAllotments();
  const { requests: ptoRequests } = usePTORequests();
  const { tasks: cadenceTasks } = useCadenceTasks();

  const [showPostForm, setShowPostForm] = useState(false);
  const [newAnn, setNewAnn] = useState({ title: '', body: '', pinned: false });
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [newLink, setNewLink] = useState({ label: '', url: '' });
  const [showDocForm, setShowDocForm] = useState(false);
  const [newDoc, setNewDoc] = useState({ label: '', url: '' });
  const [activeDocId, setActiveDocId] = useState(null);
  const [calendarsOpen, setCalendarsOpen] = useState(false);
  const [showCalForm, setShowCalForm] = useState(false);
  const [newCal, setNewCal] = useState({ label: '', url: '' });
  const [activeCalId, setActiveCalId] = useState(null);

  // ============================================================
  // SCORING STATUS — this week
  // ============================================================
  const weekDates = useMemo(() => getWeekDates(), []);
  const scoringStatus = useMemo(() => {
    const status = { totalClasses: classes.length, scoredToday: 0, weekProgress: [] };

    weekDates.forEach(dateStr => {
      let scored = 0;
      classes.forEach(cls => {
        const hasAnyScore = (cls.students || []).some(s => {
          const dayScores = s.scores?.[dateStr];
          if (!dayScores || dayScores.absent) return false;
          return VIRTUES.some(v => dayScores[v.key] > 0);
        });
        if (hasAnyScore) scored++;
      });
      status.weekProgress.push({ date: dateStr, scored, total: classes.length });
      if (dateStr === TODAY) status.scoredToday = scored;
    });

    // Students below 3.0
    let belowThree = 0;
    classes.forEach(cls => {
      (cls.students || []).forEach(s => {
        const allDayAvgs = [];
        Object.entries(s.scores || {}).forEach(([, dayScores]) => {
          if (dayScores.absent) return;
          const vals = VIRTUES.map(v => dayScores[v.key] || 0).filter(x => x > 0);
          if (vals.length > 0) allDayAvgs.push(vals.reduce((a, b) => a + b, 0) / vals.length);
        });
        if (allDayAvgs.length > 0) {
          const avg = allDayAvgs.reduce((a, b) => a + b, 0) / allDayAvgs.length;
          if (avg < 3) belowThree++;
        }
      });
    });
    status.belowThree = belowThree;

    return status;
  }, [classes, weekDates]);

  const toast = (msg) => window.dispatchEvent(new CustomEvent('toast', { detail: msg }));

  const handlePostAnnouncement = async () => {
    if (!newAnn.title.trim()) return;
    await addAnnouncement({ ...newAnn, postedBy: uid, postedByName: displayName });
    setNewAnn({ title: '', body: '', pinned: false });
    setShowPostForm(false);
    toast('Announcement posted');
  };

  const handleAddLink = async () => {
    if (!newLink.label.trim() || !newLink.url.trim()) return;
    let url = newLink.url.trim();
    if (!url.startsWith('http')) url = 'https://' + url;
    await addLink({ label: newLink.label.trim(), url });
    setNewLink({ label: '', url: '' });
    setShowLinkForm(false);
    toast('Link added');
  };

  const handleAddDoc = async () => {
    if (!newDoc.label.trim() || !newDoc.url.trim()) return;
    let url = newDoc.url.trim();
    if (!url.startsWith('http')) url = 'https://' + url;
    await addDocument({ label: newDoc.label.trim(), url });
    setNewDoc({ label: '', url: '' });
    setShowDocForm(false);
    toast('Document added');
  };

  const handleAddCalendar = async () => {
    if (!newCal.label.trim()) { toast('Please enter a calendar name'); return; }
    if (!newCal.url.trim()) { toast('Please paste the embed URL or iframe code'); return; }
    let url = newCal.url.trim();
    // Accept full <iframe ... src="..."> snippets by extracting the src URL
    const iframeMatch = url.match(/<iframe[^>]*\bsrc=["']([^"']+)["']/i);
    if (iframeMatch) url = iframeMatch[1];
    if (!url.startsWith('http')) url = 'https://' + url;
    try {
      await addCalendar({ label: newCal.label.trim(), url });
      setNewCal({ label: '', url: '' });
      setShowCalForm(false);
      toast('Calendar added');
    } catch (err) {
      console.error('Add calendar failed:', err);
      toast('Could not save calendar — ' + (err?.code || err?.message || 'unknown error'));
    }
  };

  const loading = annLoading || linksLoading || docsLoading || classesLoading;

  // Set first doc as active if none selected
  if (!activeDocId && documents.length > 0) {
    setActiveDocId(documents[0].id);
  }
  if (!activeCalId && calendars.length > 0) {
    setActiveCalId(calendars[0].id);
  }

  // Sort announcements: pinned first, then by date
  const sortedAnnouncements = [...announcements].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return 0; // already sorted by postedAt desc from Firestore
  });

  const todayName = DAY_NAMES[new Date().getDay()];
  const todayFormatted = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div>
      {/* Welcome header */}
      <div className="home-welcome">
        <div>
          <h2 className="home-greeting">Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {displayName?.split(' ')[0] || 'Teacher'}</h2>
          <div className="home-date">{todayName}, {todayFormatted}</div>
        </div>
      </div>

      {/* Admin-only compliance alert banner — appears only when items are overdue or due within 30 days */}
      {isAdmin && <HomeComplianceBanner onNavigate={onNavigate} />}

      <div className="home-grid">
        {/* ── Left Column ── */}
        <div className="home-main">

          {/* Today's Schedule (from the published schedule) */}
          <TodaysScheduleCard published={publishedSchedule} displayName={displayName} onNavigate={onNavigate} />

          {/* Scoring Status */}
          {!loading && classes.length > 0 && (
            <div className="home-card">
              <div className="home-card-header">
                <h3>This Week</h3>
                <button className="btn btn-sm btn-primary" onClick={() => onNavigate('daily')}>Go to Scoring</button>
              </div>

              {/* Today's status */}
              <div style={{ marginBottom: 16 }}>
                {scoringStatus.scoredToday < scoringStatus.totalClasses ? (
                  <div className="scoring-nudge nudge-warning">
                    You've scored {scoringStatus.scoredToday} of {scoringStatus.totalClasses} classes today.
                    <button className="btn btn-sm btn-gold" style={{ marginLeft: 8 }} onClick={() => onNavigate('daily')}>
                      Score Now
                    </button>
                  </div>
                ) : (
                  <div className="scoring-nudge nudge-done">
                    All {scoringStatus.totalClasses} classes scored today.
                  </div>
                )}
              </div>

              {/* Week progress grid */}
              <div className="week-grid">
                {weekDates.map((dateStr, idx) => {
                  const wp = scoringStatus.weekProgress[idx];
                  const isToday = dateStr === TODAY;
                  const isFuture = dateStr > TODAY;
                  const allDone = wp.scored >= wp.total && wp.total > 0;
                  const dayLabel = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'][idx];
                  return (
                    <div key={dateStr} className={`week-day ${isToday ? 'today' : ''} ${isFuture ? 'future' : ''} ${allDone ? 'done' : ''}`}>
                      <div className="week-day-label">{dayLabel}</div>
                      <div className="week-day-date">{new Date(dateStr + 'T00:00:00').getDate()}</div>
                      {!isFuture && (
                        <div className="week-day-status">
                          {wp.total === 0 ? '—' : allDone ? '✓' : `${wp.scored}/${wp.total}`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {scoringStatus.belowThree > 0 && (
                <div style={{ marginTop: 12, fontSize: 13, color: '#DC2626' }}>
                  {scoringStatus.belowThree} student{scoringStatus.belowThree !== 1 ? 's' : ''} currently below 3.0
                </div>
              )}
            </div>
          )}

          {!loading && classes.length === 0 && (
            <div className="home-card">
              <div className="home-card-header"><h3>Get Started</h3></div>
              <p style={{ color: '#6B7280', fontSize: 14 }}>
                You haven't set up any classes yet. Head to the Daily Tracker to create your first class and start scoring.
              </p>
              <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => onNavigate('daily')}>
                Set Up Classes
              </button>
            </div>
          )}

          {/* Announcements */}
          <div className="home-card">
            <div className="home-card-header">
              <h3>Announcements</h3>
              {isAdmin && (
                <button className="btn btn-sm btn-primary" onClick={() => setShowPostForm(!showPostForm)}>
                  {showPostForm ? 'Cancel' : '+ Post'}
                </button>
              )}
            </div>

            {isAdmin && showPostForm && (
              <div style={{ marginBottom: 16, padding: 14, background: '#FFFBEB', borderRadius: 8, border: '1px solid #FDE68A' }}>
                <input type="text" placeholder="Title" value={newAnn.title}
                  onChange={e => setNewAnn({ ...newAnn, title: e.target.value })}
                  style={{ marginBottom: 8, fontWeight: 600 }} />
                <textarea placeholder="Message (optional)" value={newAnn.body}
                  onChange={e => setNewAnn({ ...newAnn, body: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6,
                    fontFamily: 'var(--font-body)', fontSize: 14, resize: 'vertical', marginBottom: 8
                  }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={newAnn.pinned}
                      onChange={e => setNewAnn({ ...newAnn, pinned: e.target.checked })} />
                    Pin to top
                  </label>
                  <button className="btn btn-gold" onClick={handlePostAnnouncement}>Post</button>
                </div>
              </div>
            )}

            {annLoading ? (
              <div className="empty-state"><div className="empty-state-text">Loading...</div></div>
            ) : sortedAnnouncements.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">{'\u{1F4E2}'}</div>
                <div className="empty-state-text">{isAdmin ? 'Post your first announcement above.' : 'No announcements yet.'}</div>
              </div>
            ) : (
              <div className="announcement-list">
                {sortedAnnouncements.map(ann => (
                  <div key={ann.id} className={`announcement-item ${ann.pinned ? 'pinned' : ''}`}>
                    <div className="announcement-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {ann.pinned && <span className="pin-badge">PINNED</span>}
                        <span className="announcement-title">{ann.title}</span>
                      </div>
                      <span className="announcement-meta">{timeAgo(ann.postedAt)}</span>
                    </div>
                    {ann.body && <div className="announcement-body">{ann.body}</div>}
                    <div className="announcement-footer">
                      <span className="announcement-author">{ann.postedByName || 'Admin'}</span>
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-sm btn-secondary" onClick={() => togglePin(ann.id, ann.pinned)}>
                            {ann.pinned ? 'Unpin' : 'Pin'}
                          </button>
                          <button className="btn btn-sm" style={{ color: '#DC2626', background: 'none', padding: '4px 8px' }}
                            onClick={() => window.confirm('Delete this announcement?') && removeAnnouncement(ann.id)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Documents Viewer */}
          <div className="home-card">
            <div className="home-card-header">
              <h3>Documents</h3>
              {isAdmin && (
                <button className="btn btn-sm btn-secondary" onClick={() => setShowDocForm(!showDocForm)}>
                  {showDocForm ? 'Cancel' : '+ Add'}
                </button>
              )}
            </div>

            {isAdmin && showDocForm && (
              <div style={{ marginBottom: 12, padding: 12, background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
                <input type="text" placeholder="Document name (e.g. School Calendar)" value={newDoc.label}
                  onChange={e => setNewDoc({ ...newDoc, label: e.target.value })} style={{ marginBottom: 6 }} />
                <input type="text" placeholder="PDF URL (or path like /docs/calendar.pdf)" value={newDoc.url}
                  onChange={e => setNewDoc({ ...newDoc, url: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && handleAddDoc()} style={{ marginBottom: 8 }} />
                <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8 }}>
                  Upload PDFs to your repo's public/docs/ folder, then enter the path as: {window.location.origin}/cafm-participation-tool/docs/filename.pdf
                </div>
                <button className="btn btn-sm btn-gold" onClick={handleAddDoc}>Add Document</button>
              </div>
            )}

            {documents.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">{'\u{1F4C4}'}</div>
                <div className="empty-state-text">{isAdmin ? 'Add documents for your team to view.' : 'No documents added yet.'}</div>
              </div>
            ) : (
              <>
                {/* Document tabs */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
                  {documents.map(doc => (
                    <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <button
                        className={`btn btn-sm ${activeDocId === doc.id ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setActiveDocId(doc.id)}>
                        {doc.label}
                      </button>
                      {isAdmin && (
                        <button className="remove-btn" style={{ fontSize: 11 }}
                          onClick={() => window.confirm(`Remove "${doc.label}"?`) && removeDocument(doc.id)}>×</button>
                      )}
                    </div>
                  ))}
                </div>

                {/* PDF viewer */}
                {(() => {
                  const activeDoc = documents.find(d => d.id === activeDocId);
                  if (!activeDoc) return null;
                  return (
                    <div className="doc-viewer-container">
                      <iframe
                        src={activeDoc.url}
                        title={activeDoc.label}
                        className="doc-viewer-iframe"
                      />
                    </div>
                  );
                })()}
              </>
            )}
          </div>

          {/* Calendar — collapsible, hidden by default to keep teacher content above the fold */}
          {(isAdmin || calendars.length > 0) && (
            <div className="home-card">
              <div
                className="home-card-header"
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setCalendarsOpen(o => !o)}
              >
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, color: '#6B7280', width: 12, display: 'inline-block' }}>
                    {calendarsOpen ? '▾' : '▸'}
                  </span>
                  {'\u{1F4C5}'} Calendar
                  {!calendarsOpen && calendars.length > 0 && (
                    <span style={{ fontSize: 12, color: '#6B7280', fontWeight: 400, marginLeft: 4 }}>
                      ({calendars.map(c => c.label).join(', ')})
                    </span>
                  )}
                </h3>
                {isAdmin && calendarsOpen && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => { e.stopPropagation(); setShowCalForm(!showCalForm); }}
                  >
                    {showCalForm ? 'Cancel' : '+ Add'}
                  </button>
                )}
              </div>

              {calendarsOpen && (
                <>
                  {isAdmin && showCalForm && (
                    <div style={{ marginBottom: 12, padding: 12, background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
                      <input type="text" placeholder="Calendar name (e.g. Faculty)" value={newCal.label}
                        onChange={e => setNewCal({ ...newCal, label: e.target.value })} style={{ marginBottom: 6 }} />
                      <input type="text" placeholder='Paste full <iframe ...> code or just the URL' value={newCal.url}
                        onChange={e => setNewCal({ ...newCal, url: e.target.value })}
                        onKeyDown={e => e.key === 'Enter' && handleAddCalendar()} style={{ marginBottom: 8 }} />
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 8 }}>
                        In Google Calendar: Settings &rarr; pick the calendar &rarr; "Integrate calendar" &rarr; copy the entire "Embed code" block and paste it here. (Just the src URL also works.)
                      </div>
                      <button className="btn btn-sm btn-gold" onClick={handleAddCalendar}>Add Calendar</button>
                    </div>
                  )}

                  {calendars.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-state-text">{isAdmin ? 'Add a Google Calendar embed URL above.' : 'No calendars added yet.'}</div>
                    </div>
                  ) : (
                    <>
                      {calendars.length > 1 && (
                        <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
                          {calendars.map(cal => (
                            <div key={cal.id} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <button
                                className={`btn btn-sm ${activeCalId === cal.id ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setActiveCalId(cal.id)}>
                                {cal.label}
                              </button>
                              {isAdmin && (
                                <button className="remove-btn" style={{ fontSize: 11 }}
                                  onClick={() => window.confirm(`Remove "${cal.label}"?`) && removeCalendar(cal.id)}>×</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {calendars.length === 1 && isAdmin && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                          <button className="remove-btn" style={{ fontSize: 11 }}
                            onClick={() => window.confirm(`Remove "${calendars[0].label}"?`) && removeCalendar(calendars[0].id)}>× Remove</button>
                        </div>
                      )}
                      {(() => {
                        const activeCal = calendars.find(c => c.id === activeCalId) || calendars[0];
                        if (!activeCal) return null;
                        return (
                          <div style={{ position: 'relative', width: '100%', height: 600, border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                            <iframe
                              src={activeCal.url}
                              title={activeCal.label}
                              style={{ border: 0, width: '100%', height: '100%' }}
                              frameBorder="0"
                              scrolling="no"
                            />
                          </div>
                        );
                      })()}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Right Column / Sidebar ── */}
        <div className="home-sidebar">

          {/* Quick Links */}
          <div className="home-card">
            <div className="home-card-header">
              <h3>Quick Links</h3>
              {isAdmin && (
                <button className="btn btn-sm btn-secondary" onClick={() => setShowLinkForm(!showLinkForm)}>
                  {showLinkForm ? 'Cancel' : '+ Add'}
                </button>
              )}
            </div>

            {isAdmin && showLinkForm && (
              <div style={{ marginBottom: 12, padding: 12, background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
                <input type="text" placeholder="Label (e.g. PraxisSchool)" value={newLink.label}
                  onChange={e => setNewLink({ ...newLink, label: e.target.value })} style={{ marginBottom: 6 }} />
                <input type="text" placeholder="URL" value={newLink.url}
                  onChange={e => setNewLink({ ...newLink, url: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && handleAddLink()} style={{ marginBottom: 8 }} />
                <button className="btn btn-sm btn-gold" onClick={handleAddLink}>Add Link</button>
              </div>
            )}

            {linksLoading ? (
              <div className="empty-state"><div className="empty-state-text">Loading...</div></div>
            ) : links.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">{'\u{1F517}'}</div>
                <div className="empty-state-text">{isAdmin ? 'Add quick links for your team.' : 'No links added yet.'}</div>
              </div>
            ) : (
              <div className="quick-links-list">
                {links.map(link => (
                  <div key={link.id} className="quick-link-item">
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="quick-link-anchor">
                      {link.label}
                    </a>
                    {isAdmin && (
                      <button className="remove-btn" onClick={() => window.confirm(`Remove "${link.label}"?`) && removeLink(link.id)}>×</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* PTO Balance — visible to anyone matched to a teacher record */}
          {(() => {
            const myTeacher = publishedSchedule ? findMyTeacher(displayName, publishedSchedule.teachers || []) : null;
            const myAllotment = myTeacher ? ptoAllotments.find(a => a.id === myTeacher.id) : null;
            if (!myTeacher || !myAllotment) return null;
            const balances = computeBalances(myAllotment, ptoRequests, myTeacher.id);
            const types = [
              { key: 'sick',        label: 'Sick',        icon: '\u{1F912}' },
              { key: 'vacation',    label: 'Vacation',    icon: '\u{1F3D6}' },
              { key: 'bereavement', label: 'Bereavement', icon: '\u{1F54A}' },
            ];
            return (
              <div className="home-card">
                <div className="home-card-header">
                  <h3>Time Off Balance</h3>
                  <button className="btn btn-sm btn-secondary" onClick={() => onNavigate('pto')}>Request →</button>
                </div>
                {(myAllotment.startDate || myAllotment.endDate) && (
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8, fontStyle: 'italic' }}>
                    Contract year: {fmtPeriod(myAllotment.startDate, myAllotment.endDate)}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {types.map(t => {
                    const remaining = balances.remaining[t.key];
                    const total = balances.total[t.key];
                    const overdrawn = remaining < 0;
                    return (
                      <div key={t.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: overdrawn ? '#FEF2F2' : 'var(--gray-50)', borderRadius: 'var(--radius-sm)' }}>
                        <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>{t.icon} {t.label}</span>
                        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: overdrawn ? '#DC2626' : 'var(--navy)', fontSize: 16 }}>
                          {remaining}<span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 500 }}> / {total}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* My Cadence Tasks — pending/delayed tasks assigned to this user */}
          {(() => {
            if (!cadenceTasks || cadenceTasks.length === 0) return null;
            const dn = (displayName || '').toLowerCase();
            const dnFirst = dn.split(/\s+/)[0] || '';
            // Match if assignedTo equals displayName, or matches the role label "HM" for admin,
            // or if the user's first name appears in the assignee field.
            const myTasks = cadenceTasks.filter(t => {
              if (t.status !== 'pending' && t.status !== 'delayed') return false;
              const a = (t.assignedTo || '').toLowerCase().trim();
              if (!a) return false;
              if (a === dn) return true;
              if (dnFirst.length >= 2 && a.includes(dnFirst)) return true;
              if (isAdmin && (a === 'hm' || a === 'hm-dos' || a === 'admin')) return true;
              return false;
            }).sort((a, b) => {
              const aDate = a.dueDate || '9999-12-31';
              const bDate = b.dueDate || '9999-12-31';
              return aDate.localeCompare(bDate);
            }).slice(0, 6);
            if (myTasks.length === 0) return null;
            const today = new Date().toISOString().slice(0, 10);
            return (
              <div className="home-card">
                <div className="home-card-header">
                  <h3>My Cadence Tasks</h3>
                  {isAdmin && <button className="btn btn-sm btn-secondary" onClick={() => onNavigate('cadence')}>View all →</button>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {myTasks.map(t => {
                    const overdue = t.dueDate && t.dueDate < today;
                    return (
                      <div key={t.id} style={{
                        padding: '6px 10px',
                        background: overdue ? '#FEF2F2' : t.status === 'delayed' ? '#FEF3C7' : 'var(--gray-50)',
                        borderLeft: `3px solid ${overdue ? '#DC2626' : t.status === 'delayed' ? '#EAB308' : '#1B3A5C'}`,
                        borderRadius: 4,
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1B3A5C' }}>{t.title}</div>
                        <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>
                          {t.dueDate ? `Due ${new Date(t.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'No due date'}
                          {overdue && <span style={{ marginLeft: 6, color: '#DC2626', fontWeight: 700 }}>OVERDUE</span>}
                          {t.status === 'delayed' && <span style={{ marginLeft: 6, color: '#92400E', fontWeight: 700 }}>DELAYED</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* At a Glance */}
          <div className="home-card">
            <div className="home-card-header"><h3>At a Glance</h3></div>
            {!loading && classes.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)' }}>
                  <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>Classes</span>
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--navy)', fontSize: 18 }}>{scoringStatus.totalClasses}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)' }}>
                  <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>Scored Today</span>
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: scoringStatus.scoredToday >= scoringStatus.totalClasses ? 'var(--green)' : 'var(--gold)', fontSize: 18 }}>{scoringStatus.scoredToday}/{scoringStatus.totalClasses}</span>
                </div>
                {scoringStatus.belowThree > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'var(--red-bg)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }} onClick={() => onNavigate('dashboard')}>
                    <span style={{ fontSize: 13, color: 'var(--red)' }}>Below 3.0</span>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--red)', fontSize: 18 }}>{scoringStatus.belowThree}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-text">Set up classes to see stats</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
